import { useEffect, useState } from 'react';

/**
 * Breakpoint scale (D-18) — the single source of truth for responsive widths,
 * mirroring `breakpoints.css`. Consumed by `useBreakpoint()` and by any JS that
 * needs the numeric px thresholds, so `@media` widths never hardcode magic numbers.
 */
export const BREAKPOINTS = {
  sm: 320,
  md: 768,
  lg: 1280,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/** Largest breakpoint whose min-width fits `width` (mobile-first: `sm` is the floor). */
export function getBreakpoint(width: number): Breakpoint {
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  return 'sm';
}

/**
 * Current active breakpoint, updated on resize. SSR-safe: falls back to `lg`
 * when there is no `window` (resolved on mount).
 */
export function useBreakpoint(): Breakpoint {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(() =>
    typeof window === 'undefined' ? 'lg' : getBreakpoint(window.innerWidth),
  );

  useEffect(() => {
    const onResize = (): void => {
      setBreakpoint(getBreakpoint(window.innerWidth));
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return breakpoint;
}
